import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useStoreState } from "pullstate";

import { UIStore } from "@/store/UIStore";
import useDebounce from "@/helpers/useDebounce";
import { searchCategories } from "@/helpers/searchCategories";
import SearchIcon from "@/icons/search.svg";
import CloseIcon from "@/icons/close-small.svg";
import PageWrapper from "@/components/PageWrapper";
import { GridColumns } from "@/components/Container/Container.interface";
import Container from "@/components/Container";
import Typography from "@/components/Typography";
import IconButton from "@/components/IconButton";
import SearchCategories from "@/containers/Search/SearchCategories";
import { SearchCategory } from "@/helpers/pageHelpers/Store/interface";
import SearchArticles from "@/containers/Search/SearchArticles";
import SearchPodcasts from "@/containers/Search/SearchPodcasts";
import SearchAll from "@/containers/Search/SearchAll";
import SearchAuthors from "@/containers/Search/SearchAuthors";
import SearchEpisodes from "@/containers/Search/SearchEpisodes";
import MetaHead from "@/components/MetaHead";
import { routePath } from "@/helpers/routePath";
import { searchMetaData } from "@/helpers/pageMetaData";
import { useDataLayer } from "@/helpers/useDataLayer";
import Suggested from "@/components/EmptyState/Suggested";

import styles from "./Search.module.scss";

const Search: React.FC = () => {
  const { pushToDataLayer } = useDataLayer();
  const searchInput = useRef(document.createElement("input"));
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const debouncedQuery = useDebounce(searchQuery, 400);
  const { searchInitiated, searchCategory, searchResults } = useStoreState(
    UIStore,
    (store) => store
  );
  const router = useRouter();
  const isClientSide = typeof window !== "undefined";
  const windowWidth = isClientSide && window?.innerWidth;
  const desktopPlaceholder =
    "Szukaj artykułów, podcastów, redaktorów... co tylko chcesz.";
  const defaultPlaceholder = "Szukaj, a znajdziesz.";
  const placeholderText =
    windowWidth > 420 ? desktopPlaceholder : defaultPlaceholder;
  const pageData = {
    headerLabel: "Szukaj",
    fallbackHistoryBack: { backUrl: routePath.FEED.href },
  };
  const isSuggestedAvailable =
    searchResults &&
    searchResults[searchCategory]?.meta.totalCount === 0 &&
    searchInitiated;

  useEffect(() => {
    router.query?.q && setSearchQuery(router.query.q as string);
  }, [router.query]);

  const updateSearchPhrase = (e) => {
    setSearchQuery(e.target.value);
    e.target.value
      ? router.push({
          pathname: routePath.SEARCH.href,
          query: { q: e.target.value },
        })
      : router.push({
          pathname: routePath.SEARCH.href,
        });
    pushToDataLayer("search", { search_term: e.target.value });
  };

  const clearSearchPhrase = () => {
    UIStore.update((store) => {
      store.searchInitiated = false;
      store.searchCategory = SearchCategory.Best;
    });
    router.push({
      pathname: routePath.SEARCH.href,
    });
    setSearchQuery("");
  };

  const resolveSearch = (searchCategory, phrase) => {
    switch (searchCategory) {
      case SearchCategory.Best:
        return <SearchAll phrase={phrase} />;
      case SearchCategory.Articles:
        return <SearchArticles phrase={phrase} />;
      case SearchCategory.Podcasts:
        return <SearchPodcasts phrase={phrase} />;
      case SearchCategory.Authors:
        return <SearchAuthors phrase={phrase} />;
      case SearchCategory.Episodes:
        return <SearchEpisodes phrase={phrase} />;
    }
  };

  const changeSearchContext = (context) => {
    UIStore.update((store) => {
      store.searchCategory = context;
    });
  };

  useEffect(() => {
    const minimumQueryLength = 2;
    const queryLength = debouncedQuery.length;
    const searchInitiateStatus = queryLength >= minimumQueryLength;
    const userSearched = queryLength > 0;
    const wrongQuery = userSearched && queryLength < minimumQueryLength;

    setError(wrongQuery);

    UIStore.update((store) => {
      store.searchInitiated = searchInitiateStatus;
      store.searchCategory = SearchCategory.Best; // After query changes switch to most recent to refresh counts
    });

    // emptyQuery && UIStore.update((store) => { store.searchCategory = SearchCategory.Best });
  }, [debouncedQuery]);

  useEffect(() => {
    searchInput?.current.focus();
  }, []);

  return (
    <PageWrapper pageData={pageData} className={styles.pageWrapper}>
      <MetaHead metaData={searchMetaData} />

      <Container grid={GridColumns.Three} className={styles.container}>
        <div
          className={classNames(
            styles.search,
            searchInitiated && styles.active
          )}
        >
          <Typography variant="hero" component="h1" className={styles.heading}>
            Szukaj
          </Typography>

          <div className={styles.searchWrapper}>
            <SearchIcon className={styles.icon} />

            <input
              className={styles.searchInput}
              type="text"
              placeholder={placeholderText || defaultPlaceholder}
              onChange={updateSearchPhrase}
              value={searchQuery}
              ref={searchInput}
            />

            <IconButton
              onClick={clearSearchPhrase}
              size="small"
              className={classNames(styles.clearSearch)}
            >
              {searchQuery.length > 0 && <CloseIcon />}
            </IconButton>
          </div>

          {error && (
            <div className={styles.searchHelp}>
              Aby wyszukać treści wpisz minimum 3 znaki...
            </div>
          )}

          {searchInitiated && (
            <div className={styles.tabs}>
              {searchCategories.map((category) => {
                const isFirstTab = category.key === SearchCategory.Best;

                return (
                  <button
                    key={category.key}
                    onClick={() => changeSearchContext(category.key)}
                    className={classNames(
                      styles.tab,
                      searchCategory === category.key && styles.active
                    )}
                  >
                    {category.name}

                    {isFirstTab &&
                      searchResults &&
                      ` (${searchResults?.meta?.totalCount | 0})`}
                    {!isFirstTab &&
                      searchResults &&
                      ` (${searchResults[category.key]?.meta?.totalCount | 0})`}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Container>

      <Container grid={GridColumns.Three} className={styles.resultsContainer}>
        {searchInitiated ? (
          resolveSearch(searchCategory, debouncedQuery)
        ) : (
          <SearchCategories />
        )}
      </Container>
      {isSuggestedAvailable && <Suggested />}
    </PageWrapper>
  );
};

export default dynamic(() => Promise.resolve(Search), { ssr: false });
